import { useAdminIntakeObligations } from "@/api/center-intake-obligations";
import { useMemo, useState } from "react";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  Maximize2,
  MapPin,
  Navigation,
  PanelLeftClose,
  PanelLeftOpen,
  Route as RouteIcon,
  Truck,
  UserPlus,
} from "lucide-react";
import {
  type Operator,
  type Trip,
  TripStatus,
  type Waypoint,
} from "@/api.types.v2";
import { useActiveTrips, useTripWaypoints } from "@/api/trips";
import { useOperators } from "@/api/operators";
import {
  useAwaitingReceiptTasks,
  useUnroutedCarrierDropoffs,
} from "@/api/tasks";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { ExceptionsInbox } from "./exceptions-inbox";
import { AdminIntakeSection } from "@/features/center-intake-obligations/admin-intake-section";
import { AwaitingReceiptInbox } from "./awaiting-receipt-inbox";
import { ReadinessChip } from "@/features/dispatch/readiness-chip";
import { getTaskCareSignals } from "@/features/dispatch/selectors";
import { CareIcons } from "./care";
import { CapacityBar, LaneChips, StatusPill, tripLoadCapacity } from "./atoms";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { buildStops, type DerivedStop, etaWindow } from "./trip-stops";
import { TripActionsMenu } from "./trip-actions-menu";
import { getOperatorDisplayName, getOperatorInitials } from "@/lib/safe-access";

interface RailProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onOpenTrip: (trip: Trip) => void;
  onOpenStop: (trip: Trip, stop: DerivedStop) => void;
  onAssignDriver: (trip: Trip) => void;
}

function operatorName(op: Operator): string {
  return getOperatorDisplayName(op);
}

function operatorInitials(op: Operator): string {
  return getOperatorInitials(op);
}

function isRolling(op: Operator): boolean {
  return op.status === "ON_DUTY" && Boolean(op.isTracking);
}

function statusDot(op: Operator): {
  color: string;
  label: string;
  pulse: boolean;
} {
  if (isRolling(op)) return { color: "#16a34a", label: "Rolling", pulse: true };
  // On duty but not transmitting GPS → idle (amber), per the marker spec.
  if (op.status === "ON_DUTY")
    return { color: "#d97706", label: "Idle", pulse: false };
  if (op.status === "BLOCKED")
    return { color: "#dc2626", label: "Blocked", pulse: false };
  return { color: "#a1a1aa", label: "Off duty", pulse: false };
}

// ── stop row ────────────────────────────────────────────────────────────────
function StopRow({
  stop,
  color,
  onClick,
}: {
  stop: DerivedStop;
  color: string;
  onClick: () => void;
}) {
  const done = stop.status === "done";
  const active = stop.status === "active";
  const careTask = stop.tasks.find((t) => getTaskCareSignals(t).hasAny);
  const badge = done ? (
    <span
      className="grid h-[18px] w-[18px] place-items-center rounded-full text-white"
      style={{ background: "#94a3b8" }}
    >
      <Check className="h-3 w-3" strokeWidth={3} />
    </span>
  ) : active ? (
    <span
      className="live-dot grid h-[18px] w-[18px] place-items-center rounded-md text-white"
      style={{ background: color }}
    >
      <Navigation className="h-[10px] w-[10px]" />
    </span>
  ) : (
    <span
      className="grid h-[18px] w-[18px] place-items-center rounded-md text-[10px] font-bold tabular-nums text-white"
      style={{ background: color }}
    >
      {stop.seq}
    </span>
  );

  return (
    <button
      onClick={onClick}
      className={
        "flex w-full items-start gap-2 rounded-md py-1.5 pl-1 pr-1.5 text-left transition-colors hover:bg-indigo-50/60 " +
        (active ? "bg-indigo-50/60" : "")
      }
    >
      <div className="pt-0.5">{badge}</div>
      <div className="min-w-0 flex-1">
        <span
          className={
            "block truncate text-[12.5px] font-semibold " +
            (done ? "text-zinc-400 line-through" : "text-zinc-800")
          }
        >
          {stop.addr}
        </span>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <LaneChips counts={stop.counts} />
          {careTask && <CareIcons task={careTask} size={16} />}
        </div>
      </div>
      {(stop.window || stop.eta) && (
        <div className="shrink-0 text-right">
          {stop.window && (
            <div className="whitespace-nowrap font-mono text-[11px] font-semibold tabular-nums text-amber-600">
              {stop.window}
            </div>
          )}
          {stop.eta && (
            <div className="flex items-baseline justify-end gap-1 whitespace-nowrap font-mono tabular-nums">
              <span className="text-[8.5px] font-semibold uppercase tracking-wider text-zinc-400">
                ETA
              </span>
              <span className="text-[12px] font-semibold text-zinc-700">
                {stop.eta}
              </span>
            </div>
          )}
        </div>
      )}
    </button>
  );
}

// collapse done stops on long trips; pin the active one
function StopList({
  stops,
  color,
  onClickStop,
}: {
  stops: DerivedStop[];
  color: string;
  onClickStop: (stop: DerivedStop) => void;
}) {
  if (stops.length === 0) {
    return (
      <div className="px-2 py-2 text-[12px] text-zinc-400">No stops yet</div>
    );
  }
  return (
    <CollapsibleStops stops={stops} color={color} onClickStop={onClickStop} />
  );
}

function CollapsibleStops({
  stops,
  color,
  onClickStop,
}: {
  stops: DerivedStop[];
  color: string;
  onClickStop: (stop: DerivedStop) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Short trips (or once expanded) render the whole list.
  if (stops.length <= 14 || expanded) {
    return (
      <div className="flex flex-col">
        {stops.map((s) => (
          <StopRow
            key={s.id}
            stop={s}
            color={color}
            onClick={() => onClickStop(s)}
          />
        ))}
        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className="flex items-center gap-1 px-1 py-1.5 text-[11.5px] font-semibold text-zinc-400 hover:text-zinc-600"
          >
            <ChevronUp className="h-3.5 w-3.5" />
            Show less
          </button>
        )}
      </div>
    );
  }

  const doneCount = stops.filter((s) => s.status === "done").length;
  const startAt = Math.max(
    0,
    stops.findIndex((s) => s.status !== "done")
  );
  const windowStops = stops.slice(startAt, startAt + 6);
  const after = stops.length - (startAt + windowStops.length);
  return (
    <div className="flex flex-col">
      {doneCount > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-2 px-1 py-1.5 text-[11.5px] text-zinc-400 hover:text-zinc-600"
        >
          <span
            className="grid h-[18px] w-[18px] place-items-center rounded-full text-white"
            style={{ background: "#cbd5e1" }}
          >
            <Check className="h-3 w-3" strokeWidth={3} />
          </span>
          <span className="font-semibold">{doneCount} stops done</span>
        </button>
      )}
      {windowStops.map((s) => (
        <StopRow
          key={s.id}
          stop={s}
          color={color}
          onClick={() => onClickStop(s)}
        />
      ))}
      {after > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1 px-1 py-1.5 text-[11.5px] font-semibold text-zinc-400 hover:text-zinc-600"
        >
          <ChevronDown className="h-3.5 w-3.5" />
          {after} more stops
        </button>
      )}
    </div>
  );
}

// ── trip row ──────────────────────────────────────────────────────────────
function TripRow({
  trip,
  stops,
  defaultOpen,
  showAssign,
  onOpenTrip,
  onOpenStop,
  onAssignDriver,
}: {
  trip: Trip;
  stops: DerivedStop[];
  defaultOpen?: boolean;
  showAssign?: boolean;
  onOpenTrip: (trip: Trip) => void;
  onOpenStop: (trip: Trip, stop: DerivedStop) => void;
  onAssignDriver: (trip: Trip) => void;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const color = trip.color ?? "#6366f1";
  const stopCount = stops.length;
  const taskCount = stops.reduce((n, s) => n + s.total, 0);
  const win = etaWindow(stops);
  const cap = tripLoadCapacity(trip, taskCount);

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
      <div className="flex items-center gap-2 py-2 pl-2 pr-2">
        <span
          className="self-stretch rounded"
          style={{ width: 4, minHeight: 28, background: color }}
        />
        <button
          className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left"
          onClick={() => setOpen((v) => !v)}
        >
          <div className="flex w-full items-center gap-1.5">
            <span className="font-mono text-[12.5px] font-bold text-zinc-800">
              #{trip.tripNumber}
            </span>
            <StatusPill status={trip.status} />
            <span className="ml-auto text-[11px] tabular-nums text-zinc-500">
              {stopCount} stops · {taskCount} tasks
            </span>
          </div>
          <ReadinessChip trip={trip} waypointCount={stopCount} />
        </button>
        <div className="flex shrink-0 items-center gap-1.5 self-start">
          <button
            className="grid h-7 w-7 place-items-center rounded-md text-zinc-400 hover:bg-indigo-50 hover:text-indigo-600"
            title="Open trip"
            onClick={() => onOpenTrip(trip)}
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          <button
            className="grid h-7 w-7 place-items-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <ChevronUp className="h-[15px] w-[15px]" />
            ) : (
              <ChevronDown className="h-[15px] w-[15px]" />
            )}
          </button>
          <TripActionsMenu trip={trip} />
        </div>
      </div>

      {(cap ?? win) && (
        <div className="flex items-center gap-3 px-3 pb-2">
          {cap && (
            <CapacityBar load={cap.load} capacity={cap.capacity} compact />
          )}
          {win && (
            <span className="inline-flex items-center gap-1 font-mono text-[11px] tabular-nums text-zinc-500">
              <Clock className="h-3 w-3" />
              {win}
            </span>
          )}
        </div>
      )}

      {showAssign && (
        <div className="px-2 pb-2">
          <button
            className="flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-indigo-600 text-[12px] font-semibold text-white transition-colors hover:bg-indigo-700"
            onClick={() => onAssignDriver(trip)}
          >
            <UserPlus className="h-3.5 w-3.5" /> Assign driver
          </button>
        </div>
      )}

      {open && (
        <div className="fadeup border-t border-zinc-100 bg-zinc-50/40 px-2 pb-2 pt-0.5">
          <StopList
            stops={stops}
            color={color}
            onClickStop={(s) => onOpenStop(trip, s)}
          />
        </div>
      )}
    </div>
  );
}

// ── driver card ─────────────────────────────────────────────────────────────
function DriverCard({
  op,
  trips,
  stopsByTrip,
  defaultOpen,
  onOpenTrip,
  onOpenStop,
  onAssignDriver,
}: {
  op: Operator;
  trips: Trip[];
  stopsByTrip: Map<string, DerivedStop[]>;
  defaultOpen?: boolean;
  onOpenTrip: (trip: Trip) => void;
  onOpenStop: (trip: Trip, stop: DerivedStop) => void;
  onAssignDriver: (trip: Trip) => void;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const sm = statusDot(op);
  const color = trips[0]?.color ?? "#6366f1";
  const totalStops = trips.reduce(
    (n, t) => n + (stopsByTrip.get(t.id)?.length ?? 0),
    0
  );

  return (
    <div className="shadow-card overflow-hidden rounded-xl bg-white">
      <button
        className="flex w-full items-center gap-2.5 px-2.5 py-2.5 text-left transition-colors hover:bg-zinc-50/60"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="relative shrink-0">
          <div
            className="grid h-[34px] w-[34px] place-items-center rounded-full text-[12.5px] font-bold text-white"
            style={{ background: color }}
          >
            {operatorInitials(op)}
          </div>
          <span
            className={
              "absolute -bottom-0.5 -right-0.5 rounded-full " +
              (sm.pulse ? "live-dot" : "")
            }
            style={{
              width: 11,
              height: 11,
              background: sm.color,
              border: "2px solid #fff",
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <span className="block truncate text-[13.5px] font-bold text-zinc-900">
            {operatorName(op)}
          </span>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-zinc-500">
            {op.vehicle?.code && (
              <span className="inline-flex items-center gap-1">
                <Truck className="h-3 w-3" />
                {op.vehicle.code}
              </span>
            )}
            <span style={{ color: sm.color }} className="font-semibold">
              {sm.label}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold tabular-nums text-zinc-600">
            <span className="inline-flex items-center gap-0.5">
              <RouteIcon className="h-3 w-3 text-zinc-400" />
              {trips.length}
            </span>
            <span className="inline-flex items-center gap-0.5">
              <MapPin className="h-3 w-3 text-zinc-400" />
              {totalStops}
            </span>
          </div>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-zinc-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-zinc-400" />
        )}
      </button>
      {open && (
        <div className="fadeup flex flex-col gap-1.5 px-2 pb-2">
          {trips.length === 0 ? (
            <div className="px-2 py-2 text-[12px] text-zinc-400">No trips</div>
          ) : (
            trips.map((t, i) => (
              <TripRow
                key={t.id}
                trip={t}
                stops={stopsByTrip.get(t.id) ?? []}
                defaultOpen={i === 0 && t.status === TripStatus.ACTIVE}
                onOpenTrip={onOpenTrip}
                onOpenStop={onOpenStop}
                onAssignDriver={onAssignDriver}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── unassigned lane ─────────────────────────────────────────────────────────
function UnassignedLane({
  trips,
  stopsByTrip,
  onOpenTrip,
  onOpenStop,
  onAssignDriver,
}: {
  trips: Trip[];
  stopsByTrip: Map<string, DerivedStop[]>;
  onOpenTrip: (trip: Trip) => void;
  onOpenStop: (trip: Trip, stop: DerivedStop) => void;
  onAssignDriver: (trip: Trip) => void;
}) {
  const [open, setOpen] = useState(true);
  if (trips.length === 0) return null;
  return (
    <div
      className="overflow-hidden rounded-xl"
      style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}
    >
      <button
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className="grid h-[26px] w-[26px] place-items-center rounded-lg text-white"
          style={{ background: "#ea580c" }}
        >
          <AlertCircle className="h-[15px] w-[15px]" />
        </span>
        <span className="flex-1 text-[13px] font-bold text-zinc-900">
          Unassigned trips
        </span>
        <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-orange-700">
          {trips.length}
        </span>
        {open ? (
          <ChevronUp className="h-[15px] w-[15px] text-orange-700" />
        ) : (
          <ChevronDown className="h-[15px] w-[15px] text-orange-700" />
        )}
      </button>
      {open && (
        <div className="flex flex-col gap-1.5 px-2 pb-2">
          {trips.map((t) => (
            <TripRow
              key={t.id}
              trip={t}
              stops={stopsByTrip.get(t.id) ?? []}
              showAssign
              onOpenTrip={onOpenTrip}
              onOpenStop={onOpenStop}
              onAssignDriver={onAssignDriver}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── rail ────────────────────────────────────────────────────────────────────
export function OperatorRail({
  collapsed,
  onToggleCollapsed,
  onOpenTrip,
  onOpenStop,
  onAssignDriver,
}: RailProps) {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const scope = {
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  };

  const tripsQuery = useActiveTrips(scope);
  const trips = useMemo(() => tripsQuery.data?.data ?? [], [tripsQuery.data]);
  const waypointQueries = useTripWaypoints(trips);
  const operatorsQuery = useOperators({ ...scope, limit: 200 });
  const operators = useMemo(
    () => operatorsQuery.data?.data ?? [],
    [operatorsQuery.data]
  );

  const stopsByTrip = useMemo(() => {
    const map = new Map<string, DerivedStop[]>();
    trips.forEach((trip, i) => {
      const wps: Waypoint[] = waypointQueries[i]?.data ?? [];
      map.set(trip.id, buildStops(wps));
    });
    return map;
  }, [trips, waypointQueries]);

  const assigned = useMemo(
    () =>
      operators.map((op) => ({
        op,
        trips: trips.filter((t) => t.operatorId === op.id),
      })),
    [operators, trips]
  );
  const unassigned = useMemo(() => trips.filter((t) => !t.operatorId), [trips]);

  const [tab, setTab] = useState<"fleet" | "tasks">("fleet");
  const exceptionsQuery = useUnroutedCarrierDropoffs();
  const exceptionCount = exceptionsQuery.data?.metadata.taskCount ?? 0;
  const awaitingQuery = useAwaitingReceiptTasks();
  const awaitingCount = awaitingQuery.data?.metadata.taskCount ?? 0;
  const intakeQuery = useAdminIntakeObligations({
    page: 1,
    view: "outstanding",
    search: "",
    operatorId: "",
    sourceType: "",
    ...scope,
    dispatchCenterIds: scope.dispatchCenterIds || undefined,
  });
  const intakeCount =
    intakeQuery.isSuccess && !intakeQuery.isFetching
      ? intakeQuery.data.total
      : 0;
  const tasksTabCount = awaitingCount + exceptionCount + intakeCount;

  if (collapsed) {
    return (
      <div className="flex h-full w-12 flex-col items-center gap-1 pt-2">
        <button
          className="grid h-9 w-9 place-items-center rounded-lg text-zinc-500 hover:bg-zinc-100"
          title="Fleet"
          onClick={onToggleCollapsed}
        >
          <PanelLeftOpen className="h-[18px] w-[18px]" />
        </button>
        <div className="mt-1 flex flex-col items-center gap-1.5">
          {assigned
            .filter(({ op }) => op.status === "ON_DUTY")
            .map(({ op, trips: ts }) => (
              <div
                key={op.id}
                className="grid h-7 w-7 place-items-center rounded-full text-[10px] font-bold text-white"
                style={{ background: ts[0]?.color ?? "#6366f1" }}
                title={operatorName(op)}
              >
                {operatorInitials(op)}
              </div>
            ))}
        </div>
      </div>
    );
  }

  const loading =
    tripsQuery.isLoading ||
    operatorsQuery.isLoading ||
    waypointQueries.some((q) => q.isLoading);

  return (
    <Tabs
      value={tab}
      onValueChange={(v) => setTab(v as "fleet" | "tasks")}
      className="flex h-full flex-col gap-0 bg-zinc-50/60"
    >
      {/* tab strip — Fleet (driver board) vs Tasks (carrier-dropoff exceptions) */}
      <div className="flex items-stretch border-b border-zinc-200 bg-white">
        <TabsList
          variant="line"
          className="h-auto gap-0 rounded-none bg-transparent p-0"
        >
          <RailTrigger value="fleet" label="Fleet" count={operators.length} />
          <RailTrigger
            value="tasks"
            label="Tasks"
            count={tasksTabCount}
            countTone={tasksTabCount > 0 ? "alert" : "default"}
          />
        </TabsList>
        <div className="flex-1" />
        <button
          className="grid w-10 place-items-center text-zinc-400 hover:bg-zinc-100"
          onClick={onToggleCollapsed}
          title="Collapse"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      <TabsContent
        value="tasks"
        className="mt-0 min-h-0 flex-1 overflow-y-auto"
      >
        <AdminIntakeSection />
        <AwaitingReceiptInbox />
        <ExceptionsInbox />
      </TabsContent>
      <TabsContent
        value="fleet"
        className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 py-2">
            <UnassignedLane
              trips={unassigned}
              stopsByTrip={stopsByTrip}
              onOpenTrip={onOpenTrip}
              onOpenStop={onOpenStop}
              onAssignDriver={onAssignDriver}
            />
            {assigned.map(({ op, trips: ts }, i) => (
              <DriverCard
                key={op.id}
                op={op}
                trips={ts}
                stopsByTrip={stopsByTrip}
                defaultOpen={i < 2 && ts.length > 0}
                onOpenTrip={onOpenTrip}
                onOpenStop={onOpenStop}
                onAssignDriver={onAssignDriver}
              />
            ))}
            {assigned.length === 0 && unassigned.length === 0 && (
              <div className="px-2 py-6 text-center text-[12.5px] text-zinc-400">
                No trips or operators in scope
              </div>
            )}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

function RailTrigger({
  value,
  label,
  count,
  countTone = "default",
}: {
  value: string;
  label: string;
  count: number;
  countTone?: "default" | "alert";
}) {
  return (
    <TabsTrigger
      value={value}
      // pin to the v2 underline look: neutralize the new-york pill/heights and
      // recolor the line-variant active underline to indigo.
      className="flex-none gap-1.5 rounded-none border-0 px-3.5 py-2.5 text-[13px] font-semibold text-zinc-500 shadow-none after:inset-x-2 after:bottom-[-1px] after:h-0.5 after:rounded-full after:bg-indigo-600 hover:text-zinc-700 data-[state=active]:bg-transparent data-[state=active]:text-zinc-900 data-[state=active]:shadow-none"
    >
      {label}
      {count > 0 && (
        <span
          className={
            "rounded-full px-1.5 py-0.5 text-[10.5px] font-bold tabular-nums " +
            (countTone === "alert"
              ? "bg-orange-100 text-orange-700"
              : "bg-zinc-100 text-zinc-600")
          }
        >
          {count}
        </span>
      )}
    </TabsTrigger>
  );
}
